'use client';

import { useState, useEffect, useMemo } from 'react';
import { 
  getEnergyData, 
  calculateTotalEnergy, 
  getEnergyTypeLabel, 
  getEnergyTypes,
  EnergyType
} from '@/services/energy';
import { 
  EnergyPlant,
  SolarPlant,
  WaterPlant,
  BiomassPlant,
  BiogasPlant
} from '@/types/energy';
import { Card } from './atomic/Card';
import { LoaderPulse } from "@/components/ui/loader";

interface EnergieDatenProps {
  plz: string;
  className?: string;
}

const ErrorDisplay = ({ message }: { message: string }) => (
  <Card className="p-4 bg-red-50 border-red-200">
    <p className="text-red-600">{message}</p>
  </Card>
);

const EnergyTypeIcon = ({ type }: { type: EnergyType }) => {
  const iconProps = {
    className: "h-6 w-6",
    fill: "none",
    viewBox: "0 0 24 24",
    stroke: "currentColor"
  };

  return (
    <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center rounded-full bg-blue-100 text-blue-600">
      {type === 'sonne' && (
        <svg {...iconProps}>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      )}
      {type === 'wasser' && (
        <svg {...iconProps}>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4m0 0L4 7m16 0l-8 4m8-4v10l-8 4m0-10L4 17m0-10l8 4m0 0l8-4m0 0v10" />
        </svg>
      )}
      {(type === 'biomasse' || type === 'biogas') && (
        <svg {...iconProps}>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
      )}
    </div>
  );
};

const PlantDetails = ({ plant, type }: { plant: EnergyPlant; type: EnergyType }) => {
  const details: string[] = [];
  
  if (type === 'sonne' && 'flaecheQm' in plant) {
    const solarPlant = plant as SolarPlant;
    details.push(`${Math.round(solarPlant.flaecheQm || 0)} m² Fläche`);
  } else if (type === 'wasser' && 'istKleinwasserkraft' in plant) {
    const waterPlant = plant as WaterPlant;
    details.push(waterPlant.istKleinwasserkraft ? 'Kleinwasserkraft' : 'Großwasserkraft');
  } else if (type === 'biomasse' && 'brennstoffTyp' in plant) {
    const biomassPlant = plant as BiomassPlant;
    details.push(biomassPlant.brennstoffTyp || 'Festbrennstoff');
  } else if (type === 'biogas' && 'substratTyp' in plant) {
    const biogasPlant = plant as BiogasPlant;
    details.push(biogasPlant.substratTyp || 'Biogas');
  }

  return (
    <div className="text-xs text-gray-500">
      {details.join(' • ')}
    </div>
  );
};

export function EnergieDaten({ plz, className = '' }: EnergieDatenProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [energyData, setEnergyData] = useState<{
    wasser: EnergyPlant[];
    sonne: EnergyPlant[];
    biogas: EnergyPlant[];
    biomasse: EnergyPlant[];
  }>({
    wasser: [],
    sonne: [],
    biogas: [],
    biomasse: []
  });

  useEffect(() => {
    let isMounted = true;
    
    const loadData = async () => {
      if (!plz) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        const data = await getEnergyData(plz);
        if (isMounted) {
          setEnergyData(data);
        }
      } catch (err) {
        if (isMounted) {
          setError('Fehler beim Laden der Energiedaten. Bitte versuchen Sie es später erneut.');
          console.error('Error loading energy data:', err);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };
    
    loadData();
    
    return () => {
      isMounted = false;
    };
  }, [plz]);

  const totalEnergy = useMemo(() => ({
    wasser: calculateTotalEnergy(energyData.wasser as EnergyPlant[]),
    sonne: calculateTotalEnergy(energyData.sonne as EnergyPlant[]),
    biogas: calculateTotalEnergy(energyData.biogas as EnergyPlant[]),
    biomasse: calculateTotalEnergy(energyData.biomasse as EnergyPlant[])
  }), [energyData]);

  const getEnergyDataByType = (type: EnergyType) => energyData[type as keyof typeof energyData] || [];

  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat('de-DE').format(num);
  };

  const formatPower = (power: number): string => {
    return `${formatNumber(Math.round(power))} kW`;
  };

  const formatEnergy = (energy: number): string => {
    return energy >= 1000 
      ? `${formatNumber(Math.round(energy / 1000))} MWh`
      : `${formatNumber(Math.round(energy))} kWh`;
  };

  if (isLoading) {
    return (
      <div className={`flex flex-col items-center justify-center py-12 ${className}`}>
        <div className="flex flex-col items-center space-y-8">
          <div className="flex flex-col items-center space-y-4">
            <LoaderPulse size="lg" className="scale-150 text-blue-600 dark:text-blue-400" />
            <p className="text-gray-600 dark:text-gray-400 font-medium text-lg">Lade Energiedaten...</p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 w-full max-w-4xl">
            {['Sonne', 'Wasser', 'Biogas', 'Biomasse'].map((type) => (
              <Card key={type} className="p-6 flex flex-col items-center justify-center h-32">
                <div className="flex flex-col items-center space-y-4">
                  <LoaderPulse size="md" className="text-blue-600 dark:text-blue-400" />
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    {type}
                  </p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return <ErrorDisplay message={error} />;
  }

  if (Object.values(totalEnergy).every(energy => energy === 0)) {
    return (
      <div className={`space-y-4 ${className}`}>
        <h3 className="text-lg font-semibold text-gray-900">
          Energieerzeugung für PLZ {plz}
        </h3>
        <Card className="p-6 bg-yellow-50 border-yellow-200">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Keine Energiedaten verfügbar
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>Für die Postleitzahl {plz} konnten leider keine Energieerzeugungsdaten gefunden werden.</p>
                <ul className="list-disc pl-5 mt-2 space-y-1">
                  <li>In dieser Region sind keine Energieerzeugungsanlagen registriert</li>
                  <li>Die Daten für diese Region sind noch nicht vollständig erfasst</li>
                  <li>Die PLZ ist ungültig oder existiert nicht</li>
                </ul>
              </div>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      <h3 className="text-lg font-semibold text-gray-900">
        Energieerzeugung für PLZ {plz}
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {getEnergyTypes()
          .filter(({ key }) => totalEnergy[key] > 0)
          .map(({ key, label }) => (
            <Card key={key} className="p-4">
              <h4 className="text-sm font-medium text-gray-500">{label}</h4>
              <div className="mt-1 flex items-baseline">
                <p className="text-2xl font-semibold text-gray-900">
                  {formatEnergy(totalEnergy[key])}
                </p>
                <p className="ml-2 text-sm text-gray-500">pro Jahr</p>
              </div>
            </Card>
          ))}
      </div>

      <div className="mt-6">
        <h4 className="font-medium text-gray-900 mb-3">Detaillierte Informationen</h4>
        <div className="overflow-x-auto">
          <div className="align-middle inline-block min-w-full">
            <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Typ</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Leistung</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Jährlicher Ertrag</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {getEnergyTypes().flatMap(({ key: type }) =>
                    getEnergyDataByType(type).map((plant, index) => (
                      <tr key={`plant-${type}-${plant.id || `${index}-${plant.name}`}`} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <EnergyTypeIcon type={type} />
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {getEnergyTypeLabel(type)}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{plant.name}</div>
                          <div className="text-xs text-gray-500">{plant.ort}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{formatPower(plant.leistungKw)}</div>
                          <PlantDetails plant={plant} type={type} />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{formatEnergy(plant.ertragKwh)}</div>
                          <div className="text-xs text-gray-500">
                            Inbetriebnahme: {plant.inbetriebnahme}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            type="button"
                            className="text-blue-600 hover:text-blue-900"
                            onClick={() => {
                              console.log('Show details for', plant.id);
                            }}
                          >
                            Details
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}